import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import styled, { keyframes } from 'styled-components'
import { toast } from 'react-toastify'
import CallMergeRoundedIcon from '@mui/icons-material/CallMergeRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import { hideEmail } from 'utils/user'
import { getContractorMergeSuggestions, mergeContractors, listContractors } from 'services/contractorService'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'

const scoreOf = c => (Number(c.documentsCount) || 0) + (Number(c.tasksCount) || 0)

const pickSurvivor = members => {
  const ordered = [...members].sort((a, b) => {
    const s = scoreOf(b) - scoreOf(a)
    if (s !== 0) return s
    const at = a.createdAt ? new Date(a.createdAt).getTime() : Infinity
    const bt = b.createdAt ? new Date(b.createdAt).getTime() : Infinity
    return at - bt
  })
  return ordered[0]?.id || null
}

const contactLine = c => {
  const bits = []
  if (c.company && c.company !== c.name) bits.push(c.company)
  if (c.trade) bits.push(c.trade)
  if (c.email) bits.push(hideEmail(c.email))
  return bits.join(' · ')
}

export const MANUAL_PAGE = 30

const MergeContractorsModal = ({ open, onClose, onMerged }) => {
  const [mode, setMode] = useState('suggested')
  const [stage, setStage] = useState('pick')
  const [suggestions, setSuggestions] = useState([])
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)
  const [allContractors, setAllContractors] = useState(null)
  const [loadingAll, setLoadingAll] = useState(false)
  const [manualSearch, setManualSearch] = useState('')
  const [manualVisible, setManualVisible] = useState(MANUAL_PAGE)
  const [manualPicked, setManualPicked] = useState([])
  const [selected, setSelected] = useState([])
  const [survivorId, setSurvivorId] = useState(null)
  const [merging, setMerging] = useState(false)

  const reset = useCallback(() => {
    setMode('suggested'); setStage('pick'); setManualSearch(''); setManualPicked([])
    setSelected([]); setSurvivorId(null); setMerging(false); setAllContractors(null)
  }, [])

  const close = useCallback(() => { reset(); onClose() }, [reset, onClose])

  useEffect(() => {
    if (!open) return
    reset()
    setLoadingSuggestions(true)
    getContractorMergeSuggestions()
      .then(res => setSuggestions(res?.data?.data?.groups || []))
      .catch(() => toast.error('Could not check for possible duplicates.'))
      .finally(() => setLoadingSuggestions(false))
  }, [open, reset])

  const loadAll = useCallback(() => {
    if (allContractors || loadingAll) return
    setLoadingAll(true)
    listContractors()
      .then(res => setAllContractors(res?.data?.data?.contractors || []))
      .catch(() => toast.error('Could not load your contractors.'))
      .finally(() => setLoadingAll(false))
  }, [allContractors, loadingAll])

  useEffect(() => { if (open && mode === 'manual') loadAll() }, [open, mode, loadAll])

  const reviewGroup = members => {
    setSelected(members)
    setSurvivorId(pickSurvivor(members))
    setStage('confirm')
  }

  const manualMatches = useMemo(() => {
    const list = allContractors || []
    const q = manualSearch.trim().toLowerCase()
    if (!q) return list
    return list.filter(c => [c.name, c.company, c.trade, c.email].filter(Boolean).some(v => String(v).toLowerCase().includes(q)))
  }, [allContractors, manualSearch])

  useEffect(() => { setManualVisible(MANUAL_PAGE) }, [manualSearch, allContractors])

  const toggleManual = c => {
    setManualPicked(prev => prev.some(p => p.id === c.id) ? prev.filter(p => p.id !== c.id) : [...prev, c])
  }

  const reviewManual = () => {
    if (manualPicked.length < 2) return
    setSelected(manualPicked)
    setSurvivorId(manualPicked[0].id)
    setStage('confirm')
  }

  const survivor = selected.find(c => c.id === survivorId) || null
  const losers = selected.filter(c => c.id !== survivorId)

  const doMerge = async () => {
    if (!survivorId || losers.length === 0 || merging) return
    setMerging(true)
    try {
      await mergeContractors(survivorId, losers.map(c => c.id))
      toast.success(`Merged ${losers.length} contractor${losers.length === 1 ? '' : 's'} into ${survivor?.name || 'the record you kept'}.`)
      onMerged?.()
      close()
    } catch (err) {
      const msg = err?.response?.data?.message || 'Could not merge those contractors. Please try again.'
      toast.error(msg)
      setMerging(false)
    }
  }

  if (!open) return null

  return createPortal(
    <Backdrop onClick={close}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalHead>
          <ModalIcon><CallMergeRoundedIcon style={{ fontSize: 20 }} /></ModalIcon>
          <ModalHeadText>
            <ModalTitle>Merge duplicate contractors</ModalTitle>
            <ModalSub>Combine records for the same contractor into one. Nothing is lost: everything moves to the record you keep.</ModalSub>
          </ModalHeadText>
          <CloseBtn onClick={close} aria-label='Close'><CloseRoundedIcon style={{ fontSize: 18 }} /></CloseBtn>
        </ModalHead>

        {stage === 'pick' && (
          <>
            <Segmented>
              <SegBtn $active={mode === 'suggested'} onClick={() => setMode('suggested')}>
                <AutoAwesomeRoundedIcon style={{ fontSize: 15 }} /> Suggested
              </SegBtn>
              <SegBtn $active={mode === 'manual'} onClick={() => setMode('manual')}>Choose manually</SegBtn>
            </Segmented>

            <ModalBody>
              {mode === 'suggested' && (
                <>
                  {loadingSuggestions && [0, 1].map(i => (
                    <SkeletonGroup key={i}><SkelLine style={{ width: '40%' }} /><SkelLine style={{ width: '80%' }} /><SkelLine style={{ width: '65%' }} /></SkeletonGroup>
                  ))}
                  {!loadingSuggestions && suggestions.length === 0 && (
                    <EmptyBox>
                      <EmptyTitle>No likely duplicates found</EmptyTitle>
                      <EmptyText>We couldn’t spot any contractors with very similar names or matching contact details. You can still pick records to merge yourself.</EmptyText>
                      <GhostBtn onClick={() => setMode('manual')}>Choose manually</GhostBtn>
                    </EmptyBox>
                  )}
                  {!loadingSuggestions && suggestions.map(group => (
                    <GroupCard key={group.key}>
                      <GroupTop>
                        <GroupCount>{group.contractors.length} possible matches</GroupCount>
                        <ReviewBtn onClick={() => reviewGroup(group.contractors)}>Review &amp; merge</ReviewBtn>
                      </GroupTop>
                      {group.contractors.map(c => (
                        <MemberRow key={c.id}>
                          <MemberName>{c.name}</MemberName>
                          {contactLine(c) && <MemberMeta>{contactLine(c)}</MemberMeta>}
                          <MemberCounts>{c.documentsCount || 0} docs · {c.tasksCount || 0} tasks</MemberCounts>
                        </MemberRow>
                      ))}
                    </GroupCard>
                  ))}
                </>
              )}

              {mode === 'manual' && (
                <>
                  <SearchRow>
                    <SearchRoundedIcon style={{ fontSize: 18, color: '#9aa0ab' }} />
                    <SearchInput placeholder='Search name, company, trade, email…' aria-label='Search contractors' value={manualSearch} onChange={e => setManualSearch(e.target.value)} />
                  </SearchRow>
                  {loadingAll && [0, 1, 2].map(i => <SkelLine key={i} style={{ height: 38, margin: '6px 0' }} />)}
                  {!loadingAll && manualMatches.slice(0, manualVisible).map(c => {
                    const checked = manualPicked.some(p => p.id === c.id)
                    return (
                      <PickRow key={c.id} as='button' type='button' aria-pressed={checked} $checked={checked} onClick={() => toggleManual(c)}>
                        <PickCheck $checked={checked}>{checked && <CheckCircleRoundedIcon style={{ fontSize: 16 }} />}</PickCheck>
                        <PickText>
                          <MemberName>{c.name}</MemberName>
                          {contactLine(c) && <MemberMeta>{contactLine(c)}</MemberMeta>}
                        </PickText>
                      </PickRow>
                    )
                  })}
                  {!loadingAll && manualMatches.length === 0 && <EmptyText style={{ padding: '18px 4px' }}>No contractors match “{manualSearch}”.</EmptyText>}
                  {!loadingAll && manualMatches.length > 0 && (
                    <ShowingText>Showing {Math.min(manualVisible, manualMatches.length).toLocaleString('en-GB')} of {manualMatches.length.toLocaleString('en-GB')}</ShowingText>
                  )}
                  {!loadingAll && (
                    <InfiniteScrollSentinel hasMore={manualVisible < manualMatches.length} loading={false} onLoadMore={() => setManualVisible(v => v + MANUAL_PAGE)} />
                  )}
                </>
              )}
            </ModalBody>

            {mode === 'manual' && (
              <ModalFoot>
                <FootNote>{manualPicked.length === 0 ? 'Select two or more to merge.' : `${manualPicked.length} selected`}</FootNote>
                <PrimaryBtn disabled={manualPicked.length < 2} onClick={reviewManual}>Review selected</PrimaryBtn>
              </ModalFoot>
            )}
          </>
        )}

        {stage === 'confirm' && (
          <>
            <ModalBody>
              <ConfirmIntro>Choose the one record to <strong>keep</strong>. The others are merged into it and archived.</ConfirmIntro>
              {selected.map(c => {
                const keep = c.id === survivorId
                return (
                  <KeepRow key={c.id} $keep={keep} onClick={() => setSurvivorId(c.id)}>
                    <Radio $on={keep} />
                    <PickText>
                      <MemberName>{c.name}</MemberName>
                      {contactLine(c) && <MemberMeta>{contactLine(c)}</MemberMeta>}
                    </PickText>
                    <KeepTag $keep={keep}>{keep ? 'Keep this one' : 'Merge in & archive'}</KeepTag>
                  </KeepRow>
                )
              })}
              <SafetyBox>
                Everything the archived records hold (documents, tasks, contracts, purchase orders, insurance details and history) moves to the record you keep. Nothing is deleted, and archived contractors can be restored if needed.
              </SafetyBox>
            </ModalBody>
            <ModalFoot>
              <BackBtn onClick={() => setStage('pick')}><ArrowBackRoundedIcon style={{ fontSize: 16 }} /> Back</BackBtn>
              <PrimaryBtn disabled={!survivorId || losers.length === 0 || merging} onClick={doMerge}>
                {merging ? 'Merging…' : `Merge ${losers.length} into 1`}
              </PrimaryBtn>
            </ModalFoot>
          </>
        )}
      </ModalCard>
    </Backdrop>,
    document.body
  )
}

export default MergeContractorsModal

const modalIn = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); }`

const Backdrop = styled.div`
  position: fixed; inset: 0; z-index: 1300;
  background: rgba(14, 20, 38, 0.42);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
  @media (max-width: 560px) { align-items: flex-end; padding: 0; }
`
const ModalCard = styled.div`
  background: #fff; border: 1px solid #eceef2; border-radius: 20px;
  width: min(600px, 100%); max-height: calc(100vh - 48px);
  display: flex; flex-direction: column; overflow: hidden;
  box-shadow: 0 16px 44px rgba(12, 18, 41, 0.16);
  animation: ${modalIn} 200ms ease both;
  @media (max-width: 560px) { border-radius: 20px 20px 0 0; max-height: 92vh; }
`
const ModalHead = styled.header`
  display: flex; align-items: flex-start; gap: 12px;
  padding: 20px 20px 16px; border-bottom: 1px solid #f1f2f5;
`
const ModalIcon = styled.div`
  flex: none; width: 38px; height: 38px; border-radius: 11px;
  background: #e9f6f7; color: #1f8b98;
  display: flex; align-items: center; justify-content: center;
`
const ModalHeadText = styled.div`flex: 1; min-width: 0;`
const ModalTitle = styled.h2`margin: 0; font-size: 16px; font-weight: 800; color: #14171f;`
const ModalSub = styled.p`margin: 3px 0 0; font-size: 12.5px; line-height: 1.5; color: #6a7280;`
const CloseBtn = styled.button`
  flex: none; border: none; background: #f3f4f7; color: #6a7280;
  width: 30px; height: 30px; border-radius: 9px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  &:hover { background: #e9ebf0; }
`
const Segmented = styled.div`
  display: flex; gap: 6px; padding: 12px 20px 0;
`
const SegBtn = styled.button`
  font: inherit; display: inline-flex; align-items: center; gap: 5px;
  padding: 7px 14px; border-radius: 999px; cursor: pointer; font-size: 12.5px; font-weight: 700;
  border: 1px solid ${({ $active }) => ($active ? '#2aa6b3' : '#e2e5ea')};
  background: ${({ $active }) => ($active ? '#e9f6f7' : '#fff')};
  color: ${({ $active }) => ($active ? '#1f8b98' : '#6a7280')};
`
const ModalBody = styled.div`padding: 14px 20px; overflow-y: auto; flex: 1;`
const ModalFoot = styled.footer`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 14px 20px; border-top: 1px solid #f1f2f5;
`
const FootNote = styled.span`font-size: 12px; color: #7a8090;`
const PrimaryBtn = styled.button`
  font: inherit; padding: 10px 18px; border: none; border-radius: 999px;
  background: #2aa6b3; color: #fff; font-size: 12.5px; font-weight: 800; cursor: pointer;
  transition: background 150ms ease;
  &:hover:not(:disabled) { background: #239aa6; }
  &:disabled { background: #e7e9ee; color: #a9adb7; cursor: not-allowed; }
`
const BackBtn = styled.button`
  font: inherit; display: inline-flex; align-items: center; gap: 5px;
  padding: 9px 14px; border: 1px solid #e2e5ea; border-radius: 999px; background: #fff;
  color: #5a6172; font-size: 12.5px; font-weight: 700; cursor: pointer;
  &:hover { background: #f7f8fa; }
`
const GhostBtn = styled(BackBtn)`margin-top: 12px;`
const GroupCard = styled.div`
  border: 1px solid #eceef2; border-radius: 14px; padding: 12px 14px; margin-bottom: 12px;
`
const GroupTop = styled.div`display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;`
const GroupCount = styled.span`font-size: 11.5px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.03em; color: #8a90a0;`
const ReviewBtn = styled.button`
  font: inherit; padding: 6px 13px; border: 1px solid #2aa6b3; border-radius: 999px;
  background: #fff; color: #1f8b98; font-size: 12px; font-weight: 800; cursor: pointer;
  &:hover { background: #e9f6f7; }
`
const MemberRow = styled.div`
  padding: 8px 0; border-top: 1px solid #f4f5f8;
  &:first-of-type { border-top: none; }
`
const MemberName = styled.div`font-size: 13.5px; font-weight: 700; color: #1c2029;`
const MemberMeta = styled.div`font-size: 12px; color: #6a7280; margin-top: 2px;`
const MemberCounts = styled.div`font-size: 11.5px; color: #9298a5; margin-top: 3px;`
const SearchRow = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 9px 12px;
  border: 1px solid #e2e5ea; border-radius: 11px; margin-bottom: 10px;
`
const SearchInput = styled.input`
  border: none; outline: none; flex: 1; font: inherit; font-size: 16px; color: #1c2029; background: transparent;
`
const ShowingText = styled.div`font-size: 14px; color: #4b5563; padding: 10px 4px 2px;`
const PickRow = styled.div`
  display: flex; align-items: center; gap: 11px; padding: 9px 11px; border-radius: 11px; cursor: pointer;
  width: 100%; min-height: 44px; text-align: left; font: inherit;
  border: 1px solid ${({ $checked }) => ($checked ? '#bfe6ea' : 'transparent')};
  background: ${({ $checked }) => ($checked ? '#f2fbfb' : 'transparent')};
  &:hover { background: ${({ $checked }) => ($checked ? '#eaf8f9' : '#f7f8fa')}; }
`
const PickCheck = styled.div`
  flex: none; width: 20px; height: 20px; border-radius: 6px;
  border: 1.5px solid ${({ $checked }) => ($checked ? '#2aa6b3' : '#cfd4dd')};
  background: ${({ $checked }) => ($checked ? '#2aa6b3' : '#fff')};
  color: #fff; display: flex; align-items: center; justify-content: center;
`
const PickText = styled.div`flex: 1; min-width: 0;`
const ConfirmIntro = styled.p`margin: 2px 0 12px; font-size: 13px; line-height: 1.5; color: #454b58;`
const KeepRow = styled.div`
  display: flex; align-items: center; gap: 11px; padding: 11px; border-radius: 12px; cursor: pointer; margin-bottom: 8px;
  border: 1px solid ${({ $keep }) => ($keep ? '#2aa6b3' : '#e6e8ee')};
  background: ${({ $keep }) => ($keep ? '#f2fbfb' : '#fff')};
`
const Radio = styled.div`
  flex: none; width: 18px; height: 18px; border-radius: 50%;
  border: 2px solid ${({ $on }) => ($on ? '#2aa6b3' : '#cfd4dd')};
  box-shadow: ${({ $on }) => ($on ? 'inset 0 0 0 3px #fff, inset 0 0 0 8px #2aa6b3' : 'none')};
`
const KeepTag = styled.span`
  flex: none; font-size: 11px; font-weight: 800; padding: 4px 9px; border-radius: 999px;
  color: ${({ $keep }) => ($keep ? '#1f8b98' : '#9298a5')};
  background: ${({ $keep }) => ($keep ? '#e0f4f5' : '#f2f3f6')};
`
const SafetyBox = styled.div`
  margin-top: 6px; padding: 12px 14px; border-radius: 12px;
  background: #f6f8fa; border: 1px solid #eceef2;
  font-size: 12.5px; line-height: 1.55; color: #55606f;
`
const EmptyBox = styled.div`text-align: center; padding: 26px 12px;`
const EmptyTitle = styled.div`font-size: 14px; font-weight: 800; color: #2a2f3a;`
const EmptyText = styled.div`font-size: 12.5px; line-height: 1.55; color: #737a88; margin-top: 6px;`
const shimmer = keyframes`0% { opacity: 0.55; } 50% { opacity: 1; } 100% { opacity: 0.55; }`
const SkeletonGroup = styled.div`border: 1px solid #eef0f3; border-radius: 14px; padding: 14px; margin-bottom: 12px;`
const SkelLine = styled.div`height: 12px; border-radius: 6px; background: #eef0f3; margin: 7px 0; animation: ${shimmer} 1.3s ease-in-out infinite;`
